import React, { useEffect, useState, useCallback } from "react";
import { LangMenu } from "../i18n";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Check, Play, CircleCheck, Circle, Award, Lock, FileText, ChevronLeft, ChevronRight, ExternalLink, Youtube } from "lucide-react";
import Logo from "../components/Logo";
import api, { apiError } from "../lib/api";
import { Progress } from "../components/ui/progress";
import { toast } from "sonner";
import { journey } from "../lib/journey";

export default function Learn() {
    const { slug } = useParams();
    const navigate = useNavigate();
    const [data, setData] = useState(null);
    const [active, setActive] = useState(0);
    const [busy, setBusy] = useState(false);

    const load = useCallback(async () => {
        try { const { data } = await api.get(`/me/course/${slug}`); setData(data); }
        catch (e) {
            if (e.response?.status === 403) { const d = e.response?.data?.detail || ""; toast.error(/ended/.test(d) ? d : "Enroll in this course to start learning."); navigate(/ended/.test(d) ? "/dashboard" : `/courses/${slug}`); }
            else setData(false);
        }
    }, [slug, navigate]);

    useEffect(() => { load(); }, [load]);

    if (data === false) return <div className="min-h-screen grid place-items-center text-neutral-500">Course not found.</div>;
    if (!data) return <div className="min-h-screen grid place-items-center"><div className="h-8 w-8 rounded-full border-2 border-[#e8e1d4] border-t-ink animate-spin" /></div>;

    const lesson = data.lessons[active];
    const done = new Set(data.completed_lessons);
    const isDone = done.has(lesson.id);

    const go = (i) => { setActive(i); window.scrollTo({ top: 0, behavior: "smooth" }); };

    const markComplete = async () => {
        setBusy(true);
        try {
            const { data: res } = await api.post(`/me/course/${slug}/complete-lesson`, { lesson_id: lesson.id });
            setData((d) => ({ ...d, completed_lessons: res.completed_lessons, progress: res.progress, certificate_id: res.certificate_id }));
            journey("lesson_complete", lesson.id);
            if (res.progress === 100) { toast.success("Course complete! Your certificate is ready and on its way to your email."); navigate(`/certificate/${slug}`); }
            else if (active < data.lessons.length - 1) go(active + 1);
        } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
        finally { setBusy(false); }
    };

    return (
        <div className="min-h-screen bg-cream" data-testid="learn-page">
            <header className="border-b border-[#e8e1d4] bg-white sticky top-0 z-40">
                <div className="mx-container flex items-center justify-between h-16 gap-4">
                    <div className="flex items-center gap-4 min-w-0">
                        <Link to="/dashboard" className="text-neutral-400 hover:text-ink" aria-label="Back"><ArrowLeft className="h-5 w-5" /></Link>
                        <Logo size="sm" withTagline={false} />
                    </div>
                    <div className="flex items-center gap-3">
                        <div className="hidden sm:flex items-center gap-3 w-64">
                            <Progress value={data.progress} className="h-1.5 bg-cream flex-1" />
                            <span className="text-xs text-neutral-500 whitespace-nowrap">{data.progress}%</span>
                        </div>
                        <LangMenu />
                    </div>
                </div>
            </header>

            <div className="mx-container py-8 grid lg:grid-cols-12 gap-8">
                <div className="lg:col-span-8">
                    <div className="bg-black aspect-video overflow-hidden">
                        <video key={lesson.id} src={lesson.video} poster={lesson.thumbnail || data.course.cover} controls playsInline preload="metadata"
                            controlsList="nodownload" disablePictureInPicture onContextMenu={(e) => e.preventDefault()}
                            className="h-full w-full" data-testid="lesson-video" onPlay={() => journey("lesson_play", lesson.id)}>
                            {(lesson.subtitles || []).map((t, i) => (
                                <track key={t.lang} kind="subtitles" srcLang={t.lang} label={t.label} src={t.src} default={i === 0 && t.lang === "en"} />
                            ))}
                        </video>
                    </div>
                    {/* 3 Nov 2026 (RK): centred on phones, left-aligned from lg; buttons centred and full-width on phones */}
                    <div className="text-center lg:text-left">
                        <p className="overline mt-6">{data.course.title}</p>
                        <h1 className="font-display font-semibold text-[26px] sm:text-3xl leading-tight mt-2">{lesson.title}</h1>
                        <p className="mt-2 text-sm sm:text-base text-neutral-500">{lesson.module ? <>{lesson.module}{" · "}</> : null}Lesson {active + 1} of {data.lessons.length} · {lesson.duration}</p>
                        <div className="sm:hidden mt-3 mx-auto flex items-center gap-3 max-w-xs">
                            <Progress value={data.progress} className="h-1.5 bg-white flex-1" />
                            <span className="text-xs text-neutral-500 whitespace-nowrap">{done.size}/{data.lessons.length} done</span>
                        </div>
                        {lesson.description && <p className="mt-4 text-neutral-600 leading-relaxed max-w-2xl mx-auto lg:mx-0 whitespace-pre-line text-center lg:text-justify lg:[text-align-last:left]" data-testid="lesson-description">{lesson.description}</p>}
                    </div>
                    {(lesson.resources || []).length > 0 && (
                        <div className="mt-6 max-w-2xl mx-auto lg:mx-0 bg-white border border-[#e8e1d4]" data-testid="lesson-resources">
                            <p className="px-5 pt-4 pb-2 font-display font-semibold text-center lg:text-left">Links for this lesson</p>
                            <ul className="divide-y divide-[#efe7d6]">
                                {lesson.resources.map((r, i) => (
                                    <li key={i}>
                                        {r.url ? (
                                            <a href={r.url} target="_blank" rel="noreferrer" className="flex items-center gap-3 px-5 py-3 text-sm hover:bg-cream transition-colors">
                                                {/youtu/.test(r.url) ? <Youtube className="h-5 w-5 text-red-600 shrink-0" /> : <ExternalLink className="h-4 w-4 text-brand-golddark shrink-0" />}
                                                <span className="flex-1">{r.title}</span><span className="text-xs text-neutral-400">Open</span>
                                            </a>
                                        ) : (
                                            <p className="flex items-center justify-center lg:justify-start gap-3 px-5 py-3 text-sm text-neutral-600 text-center lg:text-left"><FileText className="h-4 w-4 text-brand-golddark shrink-0" />{r.title}</p>
                                        )}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                    {(lesson.key_points || []).length > 0 && (
                        <div className="mt-5 max-w-2xl mx-auto lg:mx-0 bg-white/70 border border-[#e8e1d4] px-5 py-4" data-testid="lesson-key-points">
                            <p className="font-display font-semibold text-center lg:text-left mb-2">In this lesson</p>
                            <ul className="space-y-2 w-fit mx-auto lg:mx-0">
                                {lesson.key_points.map((k, i) => (
                                    <li key={i} className="flex gap-3 text-sm text-neutral-700"><span className="text-brand-golddark font-semibold">{String(i + 1).padStart(2, "0")}</span><span>{k}</span></li>
                                ))}
                            </ul>
                        </div>
                    )}
                    <div className="mt-6 flex flex-col sm:flex-row flex-wrap items-stretch sm:items-center justify-center lg:justify-start gap-3 max-w-sm sm:max-w-none mx-auto lg:mx-0">
                        <button onClick={markComplete} disabled={busy || isDone} className={`justify-center ${isDone ? "btn-outline" : "btn-primary"}`} data-testid="mark-complete-btn">
                            {isDone ? <><Check className="h-4 w-4" /> Completed</> : busy ? "Saving…" : <><Check className="h-4 w-4" /> Mark as complete</>}
                        </button>
                        {lesson.sheet && (
                            <a href={lesson.sheet} target="_blank" rel="noreferrer" className="btn-outline justify-center" data-testid="lesson-sheet-btn">
                                <FileText className="h-4 w-4" /> Action Sheet (PDF)
                            </a>
                        )}
                    </div>

                    <div className="mt-4 flex items-center justify-center lg:justify-start gap-3">
                        <button onClick={() => go(Math.max(0, active - 1))} disabled={active === 0} className="btn-outline !py-2 !px-4 text-xs" data-testid="prev-lesson-btn"><ChevronLeft className="h-4 w-4" /> Previous</button>
                        <button onClick={() => go(Math.min(data.lessons.length - 1, active + 1))} disabled={active >= data.lessons.length - 1} className="btn-outline !py-2 !px-4 text-xs" data-testid="next-lesson-btn">Next <ChevronRight className="h-4 w-4" /></button>
                    </div>

                    {data.progress === 100 && (
                        <div className="mt-8 bg-espresso text-white p-6 grain flex flex-col sm:flex-row items-center justify-between gap-4 text-center sm:text-left" data-testid="learn-cert-banner">
                            <div className="flex flex-col sm:flex-row items-center gap-3">
                                <Award className="h-8 w-8 text-brand-gold" />
                                <div><p className="font-display font-semibold text-lg">You've completed the course!</p><p className="text-white/60 text-sm">Your certificate is ready, and a copy is in your email.</p></div>
                            </div>
                            <Link to={`/certificate/${slug}`} className="btn-primary" data-testid="view-certificate-btn">View certificate <Award className="h-4 w-4" /></Link>
                        </div>
                    )}
                </div>

                <aside className="lg:col-span-4">
                    <div className="bg-white border border-[#e8e1d4]">
                        <div className="px-5 py-4 border-b border-[#e8e1d4] text-center lg:text-left"><h2 className="font-display font-semibold">Course content</h2><p className="text-xs text-neutral-500 mt-0.5">{done.size} / {data.lessons.length} lessons</p></div>
                        <ul className="divide-y divide-[#efe7d6]">
                            {data.lessons.map((l, i) => {
                                const d = done.has(l.id);
                                return (
                                    <li key={l.id}>
                                        <button onClick={() => go(i)} data-testid={`lesson-item-${i}`} className={`w-full flex items-center gap-3 px-5 py-4 text-left transition-colors ${i === active ? "bg-cream" : "hover:bg-cream/60"}`}>
                                            {l.thumbnail ? <span className="relative shrink-0"><img src={l.thumbnail} alt="" loading="lazy" className="w-24 aspect-video object-cover" />{d && <CircleCheck className="absolute -top-1.5 -right-1.5 h-5 w-5 text-brand-gold bg-white rounded-full" />}</span>
                                                : d ? <CircleCheck className="h-5 w-5 text-brand-gold shrink-0" /> : <Circle className="h-5 w-5 text-neutral-300 shrink-0" />}
                                            <span className="flex-1 min-w-0">
                                                <span className="block text-sm font-medium truncate">{l.title}</span>
                                                <span className="text-xs text-neutral-400">{l.duration}</span>
                                            </span>
                                            {i === active && <Play className="h-4 w-4 text-brand-golddark shrink-0" />}
                                        </button>
                                    </li>
                                );
                            })}
                        </ul>
                    </div>
                </aside>
            </div>
        </div>
    );
}
